import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { portalExpansionApi, Program } from '../lib/portal-expansion-api';
import { useToast, getErrorMessage } from '../hooks/use-toast';
import { ToastContainer } from '../components/toast';

export default function ProgramSettingsPage() {
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const queryClient = useQueryClient();
  const { toasts, showToast, removeToast } = useToast();

  const { data: programs, isLoading, error } = useQuery({
    queryKey: ['admin-programs'],
    queryFn: portalExpansionApi.getAllPrograms,
    retry: 1,
  });

  if (error) {
    return (
      <div className="error-state">
        <h2>Unable to load programs</h2>
        <p>
          {(error as any)?.response?.status === 404
            ? 'This endpoint is not yet implemented in the backend.'
            : 'An error occurred loading the programs.'}
        </p>
        <button onClick={() => window.location.reload()} className="btn">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="loading-state">
        <div className="spinner" />
        <p>Loading programs...</p>
      </div>
    );
  }

  return (
    <>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="pagehead between">
        <div>
          <h1>Program Settings</h1>
          <p>Configure program details, legal text, and submission requirements</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          Create Program
        </button>
      </div>

      {programs && programs.length === 0 ? (
        <div className="empty-state">
          <svg
            className="icon-large"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.65}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          <h3>No programs configured</h3>
          <p>Create your first program to start accepting applications.</p>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            Create Program
          </button>
        </div>
      ) : (
        <div className="programs-grid">
          {programs?.map((program) => (
            <ProgramCard
              key={program.id}
              program={program}
              onSelect={() => setSelectedProgram(program)}
            />
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateProgramModal
          onClose={() => setShowCreateModal(false)}
          queryClient={queryClient}
          showToast={showToast}
        />
      )}

      {selectedProgram && (
        <EditProgramModal
          program={selectedProgram}
          onClose={() => setSelectedProgram(null)}
          queryClient={queryClient}
          showToast={showToast}
        />
      )}

      <style>{`
        .pagehead {
          margin-bottom: 25px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 20px;
        }

        .pagehead.between {
          flex-wrap: wrap;
        }

        .pagehead h1 {
          font-size: 30px;
          font-weight: 600;
          letter-spacing: -0.85px;
          color: #313739;
        }

        .pagehead p {
          font-size: 13px;
          margin-top: 6px;
          color: #68716d;
        }

        .btn-primary {
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          border: 0;
          background: #4b607f;
          color: white;
          cursor: pointer;
        }

        .btn-primary:hover {
          background: #3d4f68;
        }

        .error-state,
        .loading-state,
        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f2f3ee;
          border-top-color: #4b607f;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .error-state h2 {
          font-size: 18px;
          margin-bottom: 10px;
          color: #d14343;
        }

        .error-state p,
        .loading-state p {
          font-size: 13px;
          color: #68716d;
          margin-bottom: 20px;
        }

        .icon-large {
          width: 48px;
          height: 48px;
          color: #68716d;
          margin: 0 auto 16px;
        }

        .empty-state h3 {
          font-size: 16px;
          font-weight: 600;
          color: #68716d;
          margin-bottom: 8px;
        }

        .empty-state p {
          font-size: 13px;
          color: #68716d;
          margin-bottom: 20px;
        }

        .programs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 20px;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          border: 1px solid #e6e9de;
          background: white;
          color: #424a47;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn:hover {
          background: #f6f6f3;
        }

        @media (max-width: 768px) {
          .programs-grid {
            grid-template-columns: 1fr;
          }

          .pagehead {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>
    </>
  );
}

function ProgramCard({ program, onSelect }: { program: Program; onSelect: () => void }) {
  return (
    <div className="program-card" onClick={onSelect}>
      <div className="program-header">
        <h3>{program.name}</h3>
        <StatusBadge status={program.status} />
      </div>

      <p className="program-desc">{program.description || 'No description'}</p>

      <div className="program-details">
        {program.currentRelease && (
          <div className="detail-row">
            <span className="label">Amount per recipient:</span>
            <span className="value">
              {program.currentRelease.amountPerRecipient
                ? `$${program.currentRelease.amountPerRecipient.toFixed(2)}`
                : 'Not set'}
            </span>
          </div>
        )}
        <div className="detail-row">
          <span className="label">Opens:</span>
          <span className="value">
            {program.opensAt ? new Date(program.opensAt).toLocaleDateString() : 'Not scheduled'}
          </span>
        </div>
        <div className="detail-row">
          <span className="label">Closes:</span>
          <span className="value">
            {program.closesAt ? new Date(program.closesAt).toLocaleDateString() : 'No deadline'}
          </span>
        </div>
      </div>

      <button className="configure-btn">Configure →</button>

      <style>{`
        .program-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          border: 1px solid #e5e7eb;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          flex-direction: column;
        }

        .program-card:hover {
          border-color: #4b607f;
          box-shadow: 0 4px 12px rgba(75, 96, 127, 0.1);
        }

        .program-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
          gap: 12px;
        }

        .program-card h3 {
          font-size: 16px;
          font-weight: 600;
          color: #313739;
        }

        .program-desc {
          font-size: 13px;
          color: #68716d;
          margin-bottom: 16px;
          line-height: 1.5;
        }

        .program-details {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid #f2f3ee;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
        }

        .detail-row .label {
          color: #68716d;
        }

        .detail-row .value {
          color: #313739;
          font-weight: 500;
        }

        .configure-btn {
          font-size: 12px;
          color: #4b607f;
          font-weight: 500;
          background: none;
          border: 0;
          padding: 0;
          cursor: pointer;
          text-align: left;
          margin-top: auto;
        }

        .configure-btn:hover {
          color: #3d4f68;
        }
      `}</style>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: '#68716d',
    published: '#10b981',
    open: '#10b981',
    closed: '#ef4444',
  };

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: 500,
        background: `${colors[status] || '#68716d'}15`,
        color: colors[status] || '#68716d',
      }}
    >
      {status}
    </span>
  );
}

function CreateProgramModal({
  onClose,
  queryClient,
  showToast,
}: {
  onClose: () => void;
  queryClient: any;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const createMutation = useMutation({
    mutationFn: () =>
      portalExpansionApi.createProgram({
        slug,
        name,
        description,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-programs'] });
      onClose();
      showToast('Program created', 'success');
    },
    onError: (error: any) => {
      showToast(getErrorMessage(error), 'error');
    },
  });

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal">
        <div className="modal-header">
          <h2>Create Program</h2>
          <button onClick={onClose} className="modal-close" aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label>Program Slug *</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="cows-and-plows"
              disabled={createMutation.isPending}
            />
            <small>URL-friendly identifier (lowercase, hyphens only)</small>
          </div>

          <div className="form-group">
            <label>Program Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Cows and Plows Distribution"
              disabled={createMutation.isPending}
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the program"
              rows={3}
              disabled={createMutation.isPending}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button
            onClick={onClose}
            className="btn-secondary"
            disabled={createMutation.isPending}
          >
            Cancel
          </button>
          <button
            onClick={() => createMutation.mutate()}
            className="btn-primary"
            disabled={createMutation.isPending || !slug || !name}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Draft'}
          </button>
        </div>

        <style>{`
          .modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 100;
          }

          .modal {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: white;
            border-radius: 12px;
            width: 90%;
            max-width: 600px;
            max-height: 80vh;
            display: flex;
            flex-direction: column;
            z-index: 101;
          }

          .modal-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 20px 24px;
            border-bottom: 1px solid #f2f3ee;
          }

          .modal-header h2 {
            font-size: 18px;
            font-weight: 600;
            color: #313739;
          }

          .modal-close {
            font-size: 28px;
            line-height: 1;
            color: #68716d;
            background: none;
            border: 0;
            padding: 0;
            width: 32px;
            height: 32px;
            cursor: pointer;
          }

          .modal-body {
            padding: 24px;
            overflow-y: auto;
            flex: 1;
          }

          .form-group {
            margin-bottom: 16px;
          }

          .form-group label {
            display: block;
            font-size: 12px;
            font-weight: 500;
            color: #374151;
            margin-bottom: 6px;
          }

          .form-group input,
          .form-group textarea {
            width: 100%;
            padding: 10px 12px;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            font-size: 13px;
            color: #1f2937;
          }

          .form-group input:focus,
          .form-group textarea:focus {
            outline: none;
            border-color: #4b607f;
          }

          .form-group input:disabled,
          .form-group textarea:disabled {
            background: #f3f4f6;
            cursor: not-allowed;
          }

          .form-group small {
            display: block;
            font-size: 11px;
            color: #6b7280;
            margin-top: 4px;
          }

          .modal-footer {
            display: flex;
            gap: 12px;
            padding: 20px 24px;
            border-top: 1px solid #f2f3ee;
            justify-content: flex-end;
          }

          .btn-primary {
            padding: 10px 20px;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            border: 0;
            background: #4b607f;
            color: white;
            cursor: pointer;
          }

          .btn-primary:hover:not(:disabled) {
            background: #3d4f68;
          }

          .btn-primary:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .btn-secondary {
            padding: 10px 20px;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            border: 1px solid #e6e9de;
            background: white;
            color: #424a47;
            cursor: pointer;
          }

          .btn-secondary:hover {
            background: #f6f6f3;
          }
        `}</style>
      </div>
    </>
  );
}

function EditProgramModal({
  program,
  onClose,
  queryClient,
  showToast,
}: {
  program: Program;
  onClose: () => void;
  queryClient: any;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}) {
  const [name, setName] = useState(program.name);
  const [description, setDescription] = useState(program.description || '');
  const [amount, setAmount] = useState(program.currentRelease?.amountPerRecipient?.toString() || '');
  const [opensAt, setOpensAt] = useState(
    program.opensAt ? program.opensAt.slice(0, 16) : '',
  );
  const [closesAt, setClosesAt] = useState(
    program.closesAt ? program.closesAt.slice(0, 16) : '',
  );
  const [eligibilityText, setEligibilityText] = useState(program.currentRelease?.eligibilityText || '');
  const [adultReleaseText, setAdultReleaseText] = useState(program.currentRelease?.adultReleaseText || '');
  const [minorReleaseText, setMinorReleaseText] = useState(program.currentRelease?.minorReleaseText || '');
  const [eftConsentText, setEftConsentText] = useState(program.currentRelease?.eftConsentText || '');
  const [requiresWitness, setRequiresWitness] = useState(program.currentRelease?.requiresWitness || false);
  const [signatureMethod, setSignatureMethod] = useState(program.currentRelease?.signatureMethod || '');
  const [legalApproved, setLegalApproved] = useState(program.currentRelease?.legalApproved || false);
  const [submissionPrerequisites, setSubmissionPrerequisites] = useState<string[]>(
    program.currentRelease?.submissionPrerequisites || []
  );

  const updateMutation = useMutation({
    mutationFn: () =>
      portalExpansionApi.updateProgram(program.id, {
        name,
        description,
        opensAt: opensAt ? new Date(opensAt).toISOString() : undefined,
        closesAt: closesAt ? new Date(closesAt).toISOString() : undefined,
        amountPerRecipient: amount ? parseFloat(amount) : undefined,
        eligibilityText: eligibilityText || undefined,
        adultReleaseText: adultReleaseText || undefined,
        minorReleaseText: minorReleaseText || undefined,
        eftConsentText: eftConsentText || undefined,
        requiresWitness,
        signatureMethod: signatureMethod || undefined,
        legalApproved,
        submissionPrerequisites: submissionPrerequisites.length > 0 ? submissionPrerequisites : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-programs'] });
      onClose();
      showToast('Program updated', 'success');
    },
    onError: (error: any) => {
      showToast(getErrorMessage(error), 'error');
    },
  });

  const publishMutation = useMutation({
    mutationFn: () => {
      // Validate before publishing
      if (!legalApproved) {
        throw new Error('Legal text must be approved before publishing');
      }
      if (!signatureMethod) {
        throw new Error('Signature method must be configured before publishing');
      }
      if (!adultReleaseText || !eftConsentText) {
        throw new Error('Required legal text (adult release and EFT consent) must be provided');
      }
      return portalExpansionApi.publishProgram(program.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-programs'] });
      onClose();
      showToast('Program published', 'success');
    },
    onError: (error: any) => {
      showToast(getErrorMessage(error), 'error');
    },
  });

  const closeMutation = useMutation({
    mutationFn: () => portalExpansionApi.closeProgram(program.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-programs'] });
      onClose();
      showToast('Program closed', 'success');
    },
    onError: (error: any) => {
      showToast(getErrorMessage(error), 'error');
    },
  });

  const isSubmitting = updateMutation.isPending || publishMutation.isPending || closeMutation.isPending;

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal-large">
        <div className="modal-header">
          <div>
            <h2>Configure Program</h2>
            <p className="modal-subtitle">{program.slug}</p>
          </div>
          <button onClick={onClose} className="modal-close" aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="warning-box">
            <p>
              ⚠️ Legal text, signature requirements, and submission prerequisites need OCN approval before
              publication. Configure basic settings here; legal configurations will be added when approved.
            </p>
          </div>

          <div className="form-group">
            <label>Program Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label>Amount per Recipient (CAD)</label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g., 1500.00"
              disabled={isSubmitting}
            />
            <small>Leave empty if amount not yet determined</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Opens At</label>
              <input
                type="datetime-local"
                value={opensAt}
                onChange={(e) => setOpensAt(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label>Closes At</label>
              <input
                type="datetime-local"
                value={closesAt}
                onChange={(e) => setClosesAt(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <hr style={{ margin: '24px 0', border: '0', borderTop: '1px solid #f2f3ee' }} />
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Legal & Prerequisites</h3>

          <div className="form-group">
            <label htmlFor="program-eligibilityText">Eligibility Text</label>
            <textarea
              id="program-eligibilityText"
              value={eligibilityText}
              onChange={(e) => setEligibilityText(e.target.value)}
              rows={3}
              placeholder="Who is eligible for this program?"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="program-adultReleaseText">Adult Release Text</label>
            <textarea
              id="program-adultReleaseText"
              value={adultReleaseText}
              onChange={(e) => setAdultReleaseText(e.target.value)}
              rows={4}
              placeholder="Legal release text for adult recipients"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="program-minorReleaseText">Minor Release Text</label>
            <textarea
              id="program-minorReleaseText"
              value={minorReleaseText}
              onChange={(e) => setMinorReleaseText(e.target.value)}
              rows={4}
              placeholder="Legal release text for minor recipients"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="program-eftConsentText">EFT Consent Text</label>
            <textarea
              id="program-eftConsentText"
              value={eftConsentText}
              onChange={(e) => setEftConsentText(e.target.value)}
              rows={3}
              placeholder="Electronic funds transfer consent text"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="program-signatureMethod">Signature Method</label>
              <select
                id="program-signatureMethod"
                value={signatureMethod}
                onChange={(e) => setSignatureMethod(e.target.value)}
                disabled={isSubmitting}
              >
                <option value="">Not configured</option>
                <option value="typed">Typed Name</option>
              </select>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={requiresWitness}
                  onChange={(e) => setRequiresWitness(e.target.checked)}
                  disabled={isSubmitting}
                  style={{ width: 'auto', margin: 0 }}
                />
                Requires Witness
              </label>
            </div>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={legalApproved}
                onChange={(e) => setLegalApproved(e.target.checked)}
                disabled={isSubmitting}
                style={{ width: 'auto', margin: 0 }}
              />
              Legal Text Approved
            </label>
            <small>Must be checked before publishing</small>
          </div>

          <div className="form-group">
            <label>Submission Prerequisites</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                <input
                  type="checkbox"
                  checked={submissionPrerequisites.includes('membership')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSubmissionPrerequisites([...submissionPrerequisites, 'membership']);
                    } else {
                      setSubmissionPrerequisites(submissionPrerequisites.filter((p) => p !== 'membership'));
                    }
                  }}
                  disabled={isSubmitting}
                  style={{ width: 'auto', margin: 0 }}
                />
                Member must be verified
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                <input
                  type="checkbox"
                  checked={submissionPrerequisites.includes('documents')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSubmissionPrerequisites([...submissionPrerequisites, 'documents']);
                    } else {
                      setSubmissionPrerequisites(submissionPrerequisites.filter((p) => p !== 'documents'));
                    }
                  }}
                  disabled={isSubmitting}
                  style={{ width: 'auto', margin: 0 }}
                />
                Treaty card documents uploaded
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                <input
                  type="checkbox"
                  checked={submissionPrerequisites.includes('banking')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSubmissionPrerequisites([...submissionPrerequisites, 'banking']);
                    } else {
                      setSubmissionPrerequisites(submissionPrerequisites.filter((p) => p !== 'banking'));
                    }
                  }}
                  disabled={isSubmitting}
                  style={{ width: 'auto', margin: 0 }}
                />
                Banking information confirmed
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                <input
                  type="checkbox"
                  checked={submissionPrerequisites.includes('relationship')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSubmissionPrerequisites([...submissionPrerequisites, 'relationship']);
                    } else {
                      setSubmissionPrerequisites(submissionPrerequisites.filter((p) => p !== 'relationship'));
                    }
                  }}
                  disabled={isSubmitting}
                  style={{ width: 'auto', margin: 0 }}
                />
                Dependent relationships verified (if applicable)
              </label>
            </div>
            <small>Requirements that must be met before members can submit applications</small>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary" disabled={isSubmitting}>
            Cancel
          </button>
          {program.status === 'published' && (
            <button
              onClick={() => closeMutation.mutate()}
              className="btn-danger"
              disabled={isSubmitting}
            >
              Close Program
            </button>
          )}
          {program.status === 'draft' && (
            <button
              onClick={() => publishMutation.mutate()}
              className="btn-success"
              disabled={isSubmitting || !legalApproved || !signatureMethod || !adultReleaseText || !eftConsentText}
              title={
                !legalApproved
                  ? 'Legal approval required'
                  : !signatureMethod
                  ? 'Signature method required'
                  : !adultReleaseText || !eftConsentText
                  ? 'Required legal text missing'
                  : ''
              }
            >
              Publish
            </button>
          )}
          <button
            onClick={() => updateMutation.mutate()}
            className="btn-primary"
            disabled={isSubmitting || !name}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        <style>{`
          .modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 100;
          }

          .modal-large {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: white;
            border-radius: 12px;
            width: 90%;
            max-width: 700px;
            max-height: 90vh;
            display: flex;
            flex-direction: column;
            z-index: 101;
          }

          .modal-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 20px 24px;
            border-bottom: 1px solid #f2f3ee;
          }

          .modal-header h2 {
            font-size: 18px;
            font-weight: 600;
            color: #313739;
          }

          .modal-close {
            font-size: 28px;
            line-height: 1;
            color: #68716d;
            background: none;
            border: 0;
            padding: 0;
            width: 32px;
            height: 32px;
            cursor: pointer;
          }

          .modal-subtitle {
            font-size: 12px;
            color: #68716d;
            margin-top: 4px;
          }

          .modal-body {
            padding: 24px;
            overflow-y: auto;
            flex: 1;
          }

          .modal-footer {
            display: flex;
            gap: 12px;
            padding: 20px 24px;
            border-top: 1px solid #f2f3ee;
            justify-content: flex-end;
          }

          .warning-box {
            background: #fef3c7;
            border: 1px solid #fbbf24;
            border-radius: 8px;
            padding: 12px;
            margin-bottom: 20px;
          }

          .warning-box p {
            font-size: 12px;
            color: #92400e;
            line-height: 1.5;
          }

          .form-group {
            margin-bottom: 16px;
          }

          .form-group label {
            display: block;
            font-size: 12px;
            font-weight: 500;
            color: #374151;
            margin-bottom: 6px;
          }

          .form-group input,
          .form-group textarea,
          .form-group select {
            width: 100%;
            padding: 10px 12px;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            font-size: 13px;
            color: #1f2937;
          }

          .form-group input:focus,
          .form-group textarea:focus,
          .form-group select:focus {
            outline: none;
            border-color: #4b607f;
          }

          .form-group input:disabled,
          .form-group textarea:disabled,
          .form-group select:disabled {
            background: #f3f4f6;
            cursor: not-allowed;
          }

          .form-group small {
            display: block;
            font-size: 11px;
            color: #6b7280;
            margin-top: 4px;
          }

          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
          }

          .btn-secondary {
            padding: 10px 20px;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            border: 1px solid #e6e9de;
            background: white;
            color: #424a47;
            cursor: pointer;
          }

          .btn-secondary:hover {
            background: #f6f6f3;
          }

          .btn-success {
            padding: 10px 20px;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            border: 0;
            background: #10b981;
            color: white;
            cursor: pointer;
          }

          .btn-success:hover:not(:disabled) {
            background: #059669;
          }

          .btn-success:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .btn-danger {
            padding: 10px 20px;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            border: 0;
            background: #ef4444;
            color: white;
            cursor: pointer;
          }

          .btn-danger:hover:not(:disabled) {
            background: #dc2626;
          }

          .btn-danger:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          @media (max-width: 640px) {
            .modal-large {
              width: 100%;
              height: 100%;
              max-width: none;
              max-height: none;
              border-radius: 0;
            }

            .form-row {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </div>
    </>
  );
}
